import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType, type NavigateFunction } from 'react-router-dom';
import { ensureHistorySid, historySid } from '@/historySession';
import { useZoomStore } from '@/shared/store/zoomStore';

// D1 (owner decision 2026-10-15): MemoryRouter stays and the address bar keeps
// reading "/", but every in-app page gets a browser history entry carrying its
// in-app path, so Back, Forward, Alt+Left and a trackpad swipe step through the
// app's own pages instead of leaving it; F5 reopens the page (bootEntry.ts
// restoredEntry). A replace navigation (arrow-key tab travel, redirects)
// replaces the entry, so scanning tabs does not fill the history.
// Each entry is { memPath, sid, idx, prev }: sid is this session's token
// (historySession.ts; another session's entries are never replayed), idx its
// position in this session's trail and prev the page of the entry behind it.
type Entry = { memPath: string; sid: string; idx: number; prev: string | null };

function ownEntry(state: unknown, sid: string | null): Entry | null {
  if (!sid || !state || typeof state !== 'object') return null;
  const e = state as Partial<Entry>;
  return e.sid === sid && typeof e.memPath === 'string' && e.memPath.startsWith('/') ? (e as Entry) : null;
}

// A zoom-out that steps back in the browser history arrives later, by
// popstate. The zoom path is left alone until then (popTo right before the
// navigation, as on every other zoom-out): popping it early let the record's
// backstop re-seed its crumb onto the list, and the focus restore run on the
// record. Null when none is waiting. While one waits, another zoom-out is
// ignored (an auto-repeating Escape would step back past the list); it lapses
// after a second in case no step back ever comes.
let pendingZoomOut: { index: number; path: string; at: number } | null = null;
const zoomOutWaiting = (): boolean => !!pendingZoomOut && Date.now() - pendingZoomOut.at < 1000;

// A zoom-out to `path` (frame `index`; Escape, the crumb, which have no native
// Back): when the entry behind this one is that page, step back to it in the
// browser history (so a later Back does not reopen the record) and return
// true; otherwise return false and the caller pops and pushes as before.
export function backTo(path: string, index: number): boolean {
  if (zoomOutWaiting()) return true;
  if (!entryBehindIs(path)) return false;
  pendingZoomOut = { index, path, at: Date.now() };
  window.history.back();
  return true;
}

// Zoom out to frame `index` of the zoom path: back in the browser history when
// the entry behind is that page, else pop the path and push the page.
export function zoomOut(index: number, nav: NavigateFunction): void {
  const { stack, popTo } = useZoomStore.getState();
  const frame = stack[index];
  if (!frame || zoomOutWaiting() || backTo(frame.path, index)) return;
  if (popTo(index)) nav(frame.path);
}

function entryBehindIs(path: string): boolean {
  const cur = ownEntry(window.history.state, historySid());
  return !!cur && cur.idx >= 1 && cur.prev === path;
}

// The mouse back side button on a zoomed record. The browser runs its own Back
// for this button on the release (Chrome on mouseup), and only cancelling the
// release stops it. When the entry behind is the crumb's page, the native Back
// is that one step: pop the crumb (keeping the focus to restore) and let it
// run, popstate then opens the page. Otherwise cancel the press and its release
// (pointerup, mouseup, auxclick) and push the page as before, so the native
// Back cannot undo the push. With no crumb the browser handles it.
export function useMouseBackButton(): void {
  const nav = useNavigate();
  useEffect(() => {
    let cancelling: 'down' | 'up' | null = null;
    const onDown = (e: MouseEvent) => {
      if (e.button !== 3) return; // 3 = "back" side button
      cancelling = null;
      const { stack, popTo } = useZoomStore.getState();
      if (stack.length === 0) return;
      if (zoomOutWaiting()) {
        // A second press before the first one's step back arrived: swallow
        // it (and its native Back) so it cannot step past the list.
        e.preventDefault();
        e.stopPropagation();
        cancelling = 'down';
        return;
      }
      const target = stack[stack.length - 1];
      if (entryBehindIs(target.path)) {
        // The browser's own Back (on the release) makes the step; popstate
        // then pops the zoom path. With no native Back nothing changes.
        pendingZoomOut = { index: stack.length - 1, path: target.path, at: Date.now() };
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      cancelling = 'down';
      const frame = popTo(stack.length - 1);
      if (frame) nav(frame.path);
    };
    const onUp = (e: MouseEvent) => {
      if (e.button !== 3 || !cancelling) return;
      if (e.type === 'auxclick' && cancelling !== 'up') return;
      e.preventDefault();
      e.stopPropagation();
      if (e.type === 'mouseup') cancelling = 'up';
      else if (e.type === 'auxclick') cancelling = null;
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('mouseup', onUp, true);
    window.addEventListener('auxclick', onUp, true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('mouseup', onUp, true);
      window.removeEventListener('auxclick', onUp, true);
    };
  }, [nav]);
}

export function HistoryBridge(): null {
  const location = useLocation();
  const type = useNavigationType();
  const nav = useNavigate();
  const memPath = location.pathname + location.search;
  const recorded = useRef<string | null>(null);
  const page = useRef(memPath);
  // This session's position in the trail (the idx of the entry on screen), so
  // a pop knows whether it went back or forward.
  const curIdx = useRef(0);

  useEffect(() => {
    if (recorded.current === location.key) return; // StrictMode's second run
    const first = recorded.current === null;
    recorded.current = location.key;
    page.current = memPath;
    const sid = ensureHistorySid();
    const cur = ownEntry(window.history.state, sid);
    // history-2: a push to the page already open (the active tab clicked
    // again) replaces, or each click would add an entry Back seems stuck on.
    if (!first && type === 'PUSH' && cur?.memPath !== memPath) {
      curIdx.current = (cur?.idx ?? 0) + 1;
      window.history.pushState({ memPath, sid, idx: curIdx.current, prev: cur?.memPath ?? null }, '', '/');
    } else {
      curIdx.current = cur?.idx ?? 0;
      window.history.replaceState({ memPath, sid, idx: curIdx.current, prev: cur?.prev ?? null }, '', '/');
    }
  }, [location.key, memPath, type]);

  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const sid = ensureHistorySid();
      const entry = ownEntry(e.state, sid);
      const from = curIdx.current;
      if (entry) curIdx.current = entry.idx;
      // Only a zoom-out still waiting for this step counts; one whose step
      // never came (it lapsed) must not redirect a later, unrelated Back.
      const zoom = zoomOutWaiting() ? pendingZoomOut : null;
      pendingZoomOut = null;
      if (entry) {
        if (zoom) {
          const zs = useZoomStore.getState();
          const framed = zs.stack[zoom.index]?.path === zoom.path;
          if (zoom.path === entry.memPath) {
            if (framed) zs.popTo(zoom.index);
          } else if (framed && zs.popTo(zoom.index)) {
            // history-3: the entry behind had been replaced since (Back, a
            // replace there, Forward), so the step landed elsewhere: open the
            // page the crumb names, as the zoomOut fallback does.
            nav(zoom.path);
            return;
          }
        }
        // data-1/nav-1: a replace onto the page next to it (a delete back to
        // its list, RecordGone, arrow-key tab travel) can leave the same page
        // twice in a row. Landing on such a copy of the page already open
        // would be a press with nothing to show: step once more the same way,
        // without navigating. Off the start of the trail there is nothing
        // further, so it stays; past its end the browser does nothing.
        // Only a one-entry move lands on such a copy: a jump of several
        // entries (the Back button's history menu) onto the page already
        // open is the entry the user picked, so it stays, with nothing to
        // reopen.
        if (!zoom && entry.memPath === page.current) {
          if (Math.abs(entry.idx - from) === 1) {
            if (entry.idx < from) { if (entry.idx >= 1) window.history.back(); }
            else window.history.forward();
          }
          return;
        }
        // A Back of its own (keyboard, trackpad, the browser's button) onto a
        // page on the zoom path is that zoom-out: the path ends there, or the
        // page would be listed behind itself.
        if (!zoom) {
          const zs = useZoomStore.getState();
          const onPath = zs.stack.map((f) => f.path).lastIndexOf(entry.memPath);
          if (onPath >= 0) zs.popTo(onPath);
        }
        nav(entry.memPath, { replace: true });
        return;
      }
      // Another session's entry, or one from before the app: stay on this page
      // and make the entry ours, so it never reopens a page from that session.
      window.history.replaceState({ memPath: page.current, sid, idx: 0, prev: null }, '', '/');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [nav]);

  return null;
}
